import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckIcon, HomeIcon, UserGroupIcon, ChevronDownIcon, ArrowPathIcon, ShieldCheckIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useAuth } from '../../hooks/useAuth';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { toast } from 'react-hot-toast';
import { WorkspaceData, ProfileData } from '../../types/auth.types';
import { Fragment } from 'react';
import { AuthService } from '../../services/auth.service';
import { useDispatch } from 'react-redux';
import { logout, setCredentials, setAuthActiveWorkspace } from '../../store/slices/authSlice';
import { setActiveWorkspace } from '../../store/slices/workspaceSlice';
import { config } from '../../config';

interface WorkspaceSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

// Custom modal component to replace Dialog from headlessui
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer: React.ReactNode
}) => {
  if (!isOpen) return null;

 

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose}></div>
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <div className="w-full max-w-md transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
          <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">{title}</h3>
          <div className="mt-4">
            {children}
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            {footer}
          </div>
        </div>
      </div>
    </div>
  );
};

const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({ isOpen, onClose }) => {
  const { workspaces, activeWorkspace, userProfiles, loading, setActiveWorkspace: contextSetActiveWorkspace, createWorkspace, leaveWorkspace, deleteWorkspace } = useWorkspace();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceDescription, setNewWorkspaceDescription] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmLeaveId, setConfirmLeaveId] = useState<string | null>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({});
  const [switching, setSwitching] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [switchingWorkspaceId, setSwitchingWorkspaceId] = useState<string | null>(null);
  const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');

  const sidebarRef = React.useRef<HTMLDivElement>(null);
  useOnClickOutside(sidebarRef, onClose);

  // Filter out personal workspaces - ensure workspaces is an array
  const teamWorkspaces = Array.isArray(workspaces) ? workspaces.filter(workspace => !workspace.is_personal) : [];

  const handleSwitchWorkspace = async (workspaceId: string) => {
    try {
      setSwitchingWorkspaceId(workspaceId);

      // Find the current profile
      const currentProfile = userProfiles.find(profile => profile.is_current_user);
      if (currentProfile) {
        await switchToWorkspace(currentProfile, workspaceId);
      } else {
        // Use the context function instead of direct API call
        const success = await contextSetActiveWorkspace(workspaceId);

        if (success) {
          window.postMessage(
            {
              type: "FROM_WEB_ACTIVE_WORKSPACE",
              payload: { activeWorkspaceId: workspaceId },
            },
            "*"
          );

          // Close the sidebar
          onClose();

          // Show the loading overlay
          setLoadingMessage('Switching workspace...');
          setIsWorkspaceLoading(true);

          // Wait 1.5 seconds then reload the page to ensure fresh state
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          toast.error('Failed to switch workspace');
        }
      }
    } catch (err: any) {
      console.error('Error switching workspace:', err);
      toast.error(err?.message || 'Error switching workspace');
    } finally {
      setSwitchingWorkspaceId(null);
    }
  };

  const switchToWorkspace = async (profile: ProfileData, workspaceId: string) => {
    try {
      // If it's the same profile, just switch workspace
      if (profile.is_current_user) {
        setSwitchingWorkspaceId(workspaceId);

        // Use the context function that handles Redux updates
        const success = await contextSetActiveWorkspace(workspaceId);

        if (success) {
          window.postMessage(
            {
              type: "FROM_WEB_ACTIVE_WORKSPACE",
              payload: { activeWorkspaceId: workspaceId },
            },
            "*"
          );

          // Close the sidebar
          onClose();

          // Show the loading overlay
          setLoadingMessage('Switching workspace...');
          setIsWorkspaceLoading(true);

          // Wait 1.5 seconds then reload the page
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          toast.error('Failed to switch workspace');
        }

        setSwitchingWorkspaceId(null);
        return;
      }

      // If it's a different profile, switch profile first
      await switchToProfile(profile.user_id, workspaceId);
    } catch (err: any) {
      console.error('Error switching workspace:', err);
      toast.error(err?.message || 'Error switching workspace');
      setSwitchingWorkspaceId(null);
    }
  };

  const switchToProfile = async (profileId: string, workspaceId?: string) => {
    try {
      setSwitching(true);
      setSwitchingId(profileId);

      const result = await AuthService.switchProfile(profileId);
      if (result.success) {
        // First, clear the current user's session
        logout();

        // Clear any cached data for the previous user
        sessionStorage.clear();

        // Update localStorage
        localStorage.setItem('token', result.data.token);
        localStorage.setItem('userId', result.data.user.id);

        // Get encryption keys from the result
        const encryptedVaultKey = result.data.encryptedVaultKey;

        // Update Redux state with new user info
        dispatch(setCredentials({
          user: {
            id: result.data.user.id,
            email: result.data.user.email,
            two_factor_enabled: false
          },
          token: result.data.token,
          role_id: result.data.role_id,
          team_id: result.data.team_id,
          permissions: result.data.permissions
        }));

        // If workspace specified, set it as active after login
        if (workspaceId) {
          try {
            await AuthService.setActiveWorkspace(workspaceId);
          } catch (err) {
            console.error('Error setting active workspace:', err);
          }
        }

        // CRITICAL: Update the browser extension with new auth data
        if (window.chrome?.runtime?.id) {
          try {
            // Show a loading indicator for extension sync
            const syncToast = toast.loading('Syncing with extension...');

            chrome.runtime.sendMessage(config.EXTENSION_ID, {
              type: 'SAVE_AUTH_DATA',
              payload: {
                user: {
                  id: result.data.user.id,
                  email: result.data.user.email,
                  name: result.data.user.name || ''  // Include name if available
                },
                token: result.data.token,
                isAuthenticated: true,
                role_id: result.data.role_id,
                permissions: result.data.permissions,
                team_id: result.data.team_id,
                encryptedVaultKey: result.data.encryptedVaultKey,
                // Make sure to include ALL encryption keys
                masterKey: result.data.masterKey,
                // Add timestamp to ensure extension recognizes this as a new switch
                timestamp: new Date().getTime()
              }
            }, (response) => {
              // Always dismiss the loading toast
              toast.dismiss(syncToast);

              if (response && response.success) {
                // Extension successfully updated
                toast.success('Extension synced successfully', {
                  duration: 3000
                });
              } else {
                // Handle unexpected response
                console.error('Extension returned an error:', response?.error);
                toast.error('Extension sync returned an error', {
                  duration: 5000
                });
              }
            });
          } catch (err) {
            console.error('Error communicating with extension:', err);
            toast.error('Failed to sync with extension', {
              duration: 5000
            });
          }
        }

        toast.success('Switching to profile...');

        // Close the sidebar
        onClose();

        // Show the loading overlay
        setLoadingMessage('Switching profile...');
        setIsWorkspaceLoading(true);

        // Force page reload after a short delay to ensure all Redux state is updated
        setTimeout(() => {
          // window.location.reload();
          setIsWorkspaceLoading(false);
        }, 1500);
      } else {
        toast.error('Failed to switch profile');
        setSwitching(false);
        setSwitchingId(null);
      }
    } catch (err: any) {
      console.error('Error switching profile:', err);
      toast.error(err?.message || 'Error switching profile');
      setSwitching(false);
      setSwitchingId(null);
    }
  };

  const toggleProfileExpansion = (profileId: string) => {
    setExpandedAccounts(prev => ({
      ...prev,
      [profileId]: !prev[profileId]
    }));
  };

  // Helper to truncate email for display
  const getEmailInitial = (email?: string): string => {
    if (!email) return 'U';
    return email.charAt(0).toUpperCase();
  };

  const handleLogout = () => {
    try {
        logout();
        navigate('/login', { replace: true });
    } catch (error) {
        console.error('Logout error:', error);
        toast.error('Failed to logout');
    }
};

  const maskEmail = (email: string) => {
    const [username, domain] = email.split('@');
    if (username.length <= 3) return email; // Return as is if username is 3 characters or less

    let maskedUsername = '';
    for (let i = 0; i < username.length; i++) {
        if (i < 3) {
            maskedUsername += username[i]; // Keep the first three characters
        } else if ((i - 3) % 3 === 0) {
            maskedUsername += '*'; // Mask every third character after the first three
        } else {
            maskedUsername += username[i]; // Keep the other characters
        }
    }
    return `${maskedUsername}@${domain}`;
  };

//   const maskEmail = (email: string) => {
//     const [username, domain] = email.split('@');
//     if (username.length <= 2) return email; // Return as is if username is 2 characters or less
//     const maskedUsername = username.substring(0, 2) + '*'.repeat(username.length - 2);
//     return `${maskedUsername}@${domain}`;
// };


  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div
            className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity"
            onClick={onClose}
          />

          <div className="fixed right-0 max-w-sm w-full flex">
            <div
              ref={sidebarRef}
              className={`relative w-full max-w-sm bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-2xl flex flex-col h-full transform transition-transform duration-300 border-l border-gray-200/50 dark:border-gray-700/50 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
            >
              {/* Modern Header with Gradient */}
              <div className="relative px-6 py-5 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 border-b border-white/20">
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
                <div className="relative flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg">
                      <UserGroupIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">{activeWorkspace?.name || "Workspaces"}</h2>
                      <p className="text-xs text-white/70 mt-0.5">Manage your workspaces</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all duration-200 backdrop-blur-sm border border-white/20"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto bg-gray-50/30 dark:bg-gray-800/20">
                {/* Current User Section - Enhanced */}
                <div className="px-6 py-4 bg-white/50 dark:bg-gray-800/30 backdrop-blur-sm border-b border-gray-200/50 dark:border-gray-700/50">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                        {user?.email?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white shadow-sm"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Signed in as</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate mt-0.5">
                        {user?.email ? maskEmail(user.email) : ''}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Workspaces Section - Modern Cards */}
                <div className="px-4 py-4">
                  <div className="flex justify-between items-center px-2 py-2 mb-3">
                    <h3 className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider flex items-center">
                      <div className="w-2 h-2 bg-primary-500 rounded-full mr-2"></div>
                      Workspaces
                    </h3>
                  </div>

                  {loading ? (
                    <div className="p-6 text-center">
                      <div className="relative w-12 h-12 mx-auto mb-4">
                        <div className="absolute top-0 left-0 w-full h-full border-4 border-primary-200 dark:border-primary-800 rounded-full animate-ping opacity-75"></div>
                        <div className="absolute top-0 left-0 w-full h-full border-4 border-primary-400 dark:border-primary-600 rounded-full animate-pulse"></div>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">Loading workspaces...</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {teamWorkspaces.map((workspace) => (
                        <div key={workspace.id} className="relative group">
                          <button
                            onClick={() => {
                              const currentProfile = userProfiles.find(profile => profile.is_current_user);
                              if (currentProfile) {
                                switchToWorkspace(currentProfile, workspace.id);
                              } else {
                                handleSwitchWorkspace(workspace.id);
                              }
                            }}
                            className={`flex items-center w-full px-4 py-4 text-sm rounded-2xl transition-all duration-300 transform hover:scale-[1.02] ${workspace.is_active
                                ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25 border border-primary-400'
                                : 'bg-white/70 dark:bg-gray-800/50 hover:bg-white dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-700/50 hover:shadow-lg hover:shadow-gray-500/10'
                              } backdrop-blur-sm`}
                            disabled={switching || switchingWorkspaceId === workspace.id}
                          >
                            <div className={`p-2 rounded-xl mr-4 transition-all duration-200 ${
                              workspace.is_active 
                                ? 'bg-white/20 border border-white/30' 
                                : 'bg-primary-100 dark:bg-primary-900/40 group-hover:bg-primary-200 dark:group-hover:bg-primary-800/50'
                            }`}>
                              <UserGroupIcon className={`h-5 w-5 ${
                                workspace.is_active 
                                  ? 'text-white' 
                                  : 'text-primary-600 dark:text-primary-400'
                              }`} />
                            </div>
                            <div className="flex-1 text-left">
                              <span className="font-semibold truncate block">
                                {workspace.name}
                              </span>
                              {workspace.is_active && (
                                <span className="text-xs text-white/80 mt-0.5 block">
                                  Currently active
                                </span>
                              )}
                            </div>
                            {workspace.is_active && (
                              <div className="p-1 bg-white/20 rounded-lg">
                                <CheckIcon className="h-4 w-4 text-white" />
                              </div>
                            )}
                            {switchingWorkspaceId === workspace.id && (
                              <div className="p-1">
                                <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></div>
                              </div>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {teamWorkspaces.length === 0 && !loading && (
                    <div className="px-4 py-8 text-center bg-white/50 dark:bg-gray-800/30 rounded-2xl backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
                      <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-2xl w-fit mx-auto mb-3">
                        <UserGroupIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                        No workspaces found
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        Create or join a workspace to get started
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Buttons - Modern Style */}
                <div className="px-4 py-4 mt-auto bg-white/50 dark:bg-gray-800/30 backdrop-blur-sm border-t border-gray-200/50 dark:border-gray-700/50">
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        navigate('/mfa-setup');
                      }}
                      className="group w-full text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 
                               hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 dark:hover:from-primary-900/20 dark:hover:to-primary-800/20 
                               hover:text-primary-700 dark:hover:text-primary-300 rounded-2xl transition-all duration-300 
                               flex items-center space-x-3 border border-transparent hover:border-primary-200 dark:hover:border-primary-700/50
                               hover:shadow-lg hover:shadow-primary-500/10 transform hover:scale-[1.02]"
                    >
                      <div className="p-2 bg-primary-100 dark:bg-primary-900/40 rounded-xl group-hover:bg-primary-200 dark:group-hover:bg-primary-800/50 transition-all duration-300">
                        <ShieldCheckIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">Setup MFA</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                          Enhance security
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                      }}
                      className="group w-full text-left px-4 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 
                               hover:bg-gradient-to-r hover:from-red-50 hover:to-red-100 dark:hover:from-red-900/20 dark:hover:to-red-800/20 
                               hover:text-red-700 dark:hover:text-red-300 rounded-2xl transition-all duration-300 
                               flex items-center space-x-3 border border-transparent hover:border-red-200 dark:hover:border-red-700/50
                               hover:shadow-lg hover:shadow-red-500/10 transform hover:scale-[1.02]"
                    >
                      <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-xl group-hover:bg-red-200 dark:group-hover:bg-red-800/50 transition-all duration-300">
                        <ArrowRightOnRectangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold">Sign Out</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                          End your session
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Switching Loading Overlay */}
      {switching && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl p-8 rounded-3xl shadow-2xl text-center max-w-xs w-full mx-auto border border-gray-200/50 dark:border-gray-700/50">
            <div className="relative w-16 h-16 mx-auto mb-6">
              <div className="absolute top-0 left-0 w-full h-full border-4 border-primary-200 dark:border-primary-800 rounded-full animate-ping opacity-75"></div>
              <div className="absolute top-0 left-0 w-full h-full border-4 border-primary-500 dark:border-primary-600 rounded-full animate-pulse"></div>
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Switching profile...</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Please wait while we load your data</p>
          </div>
        </div>
      )}

      {/* Workspace Loading Overlay - Enhanced */}
      {isWorkspaceLoading && (
        <div className="fixed inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 bg-opacity-95 backdrop-blur-sm flex flex-col items-center justify-center z-50">
          <div className="relative w-24 h-24 mb-8">
            <div className="absolute top-0 left-0 w-full h-full border-4 border-primary-300/50 rounded-full animate-ping opacity-75"></div>
            <div className="absolute top-0 left-0 w-full h-full border-4 border-primary-400/70 rounded-full animate-pulse"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
                <UserGroupIcon className="w-10 h-10 text-white" />
              </div>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">{loadingMessage}</h2>
          <p className="text-primary-200 text-lg mb-6">Preparing your workspace...</p>
          <div className="flex space-x-2">
            <div className="w-3 h-3 bg-primary-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-3 h-3 bg-primary-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-primary-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkspaceSidebar;